// Unpublished Work (c) 2023-2024 Deere & Company.
import { Alert } from "@deere/fuel-react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOktaAuth } from "@okta/okta-react";
import { AxiosError } from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router";

import OrderApi from "../../../api/orders/orderApi";
import useOrderApi from "../../../api/orders/useOrderApi";
import pricingApi from "../../../api/pricing/pricingApi";
import {
  ConsolidatedPriceResponse,
  OrderPriceRequest,
  PricingTaxField,
  PricingWithTaxDetailsRequest,
} from "../../../api/pricing/pricingInterfaces";
import usePricingApi from "../../../api/pricing/usePricingApi";
import ErrorPage from "../../../components/ErrorPage/ErrorPage";
import LoadingIndicator from "../../../components/LoadingIndicator/LoadingIndicator";
import { ConfigContext } from "../../../context/ConfigContext";
import { DealerAccountContext } from "../../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { PreferenceContext } from "../../../context/PreferencesContext/PreferencesContext";
import { CATEGORIES, TOGGLES } from "../../featureToggles/constants";
import OrderNotFoundError from "../components/OrderNotFoundError/OrderNotFoundError";
import { getServiceCodeForSLAName } from "../components/PriceModal/utils";
import {
  Order,
  OrderLineItem,
  OrderStatus,
  urlParamsToOrderTypeMapping,
} from "../types";
import {
  convertToNewOrderType,
  createOrderLineItemsFromNewOrderType,
  getOrderCurrencyCode,
  getOrderCurrencySymbol,
  isCodedPricePreferred,
} from "../utils";

const isJDPSToggleEnabled = (
  isToggleEnabled: (featureName: string, toggleName: string) => boolean,
  enableFeatureToggle: boolean
) =>
  enableFeatureToggle &&
  isToggleEnabled(CATEGORIES.EXTREME_UI, TOGGLES.ENABLE_MARKUP_PRICE);

const EstimatedCostSummary = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation(["orders", "common"]);
  const { oktaAuth } = useOktaAuth();
  const { dealerAccount } = useContext(DealerAccountContext);
  const { orderType, orderNumber } = useParams();
  const {
    PRICING_SERVICE_URL,
    CONSOLIDATED_PRICE_API_MAX_PARALLEL_CALLS,
    CONSOLIDATED_PRICE_API_BATCH_SIZE,
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_PRICING,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_ORDER_SERVICE,
    ENABLE_NEW_SLA_DATA_FOR_PRICING,
    ENABLE_USE_ORDER_RESPONSE_CURRENCY_CODE = false,
  } = useContext(ConfigContext);
  const dealerLocation = dealerAccount?.location?.country;
  const [consolidatedPriceResponse, setConsolidatedPriceResponse] =
    useState<ConsolidatedPriceResponse>();
  const [orderCurrencyCode, setOrderCurrencyCode] = useState<string>();
  const { ORDERS_SERVICE_URL } = useContext(ConfigContext);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [isTechnicalError, setIsTechnicalError] = useState(false);
  const [apiError, setApiError] = useState<AxiosError>();
  const { userPreferences } = useContext(PreferenceContext);
  const maskPrice =
    isCodedPricePreferred(userPreferences) ||
    isJDPSToggleEnabled(isToggleEnabled, ENABLE_FEATURE_TOGGLE_CONTEXT_V2);
  const currencyCode = getOrderCurrencyCode(
    orderCurrencyCode,
    dealerLocation,
    dealerAccount?.mruSalesOrg,
    ENABLE_USE_ORDER_RESPONSE_CURRENCY_CODE
  );
  const currencySymbol = getOrderCurrencySymbol(
    orderCurrencyCode,
    dealerLocation,
    dealerAccount?.mruSalesOrg,
    ENABLE_USE_ORDER_RESPONSE_CURRENCY_CODE
  );
  const costEstimationHeaderSx = {
    fontWeight: theme.tokens.FuelFontWeightSemibold,
    fontSize: theme.tokens.FuelFontSizeHeading1,
    color: theme.tokens.FuelColorTextPrimary,
    marginLeft: theme.tokens.FuelSpacing4,
  };
  const costHeaderSx = {
    fontWeight: theme.tokens.FuelFontWeightSemibold,
    fontSize: theme.tokens.FuelFontSizeHeading4,
  };
  const costBreakDownHeaderSx = {
    ...costHeaderSx,
    paddingBottom: theme.tokens.FuelSpacing4,
  };
  const costBreakDownSectionSx = {
    ...costHeaderSx,
    fontSize: theme.tokens.FuelFontSizeSm,
    color: theme.tokens.FuelColorTextTertiary,
    paddingBottom: theme.tokens.FuelSpacing4,
  };
  const costBreakDownValueSx = {
    ...costHeaderSx,
    fontSize: theme.tokens.FuelFontSizeSm,
    paddingBottom: theme.tokens.FuelSpacing4,
    float: "right",
  };
  const costBreakDownTotalValueSx = {
    ...costBreakDownValueSx,
    fontSize: theme.tokens.FuelFontSizeHeading4,
  };

  const isPricingApiRbacAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_PRICING;
  const pricingApiHook = usePricingApi();

  const consolidatedPricingFn = isPricingApiRbacAuthEnabled
    ? pricingApiHook.getConsolidatedPriceDetails
    : (consolidatedPricingReq: PricingWithTaxDetailsRequest[]) =>
        pricingApi.getConsolidatedPriceDetails(
          consolidatedPricingReq,
          PRICING_SERVICE_URL,
          getAccessToken()
        );

  const orderPricingFn = isPricingApiRbacAuthEnabled
    ? pricingApiHook.getOrderPriceDetails
    : (orderPriceRequests: OrderPriceRequest) =>
        pricingApi.getOrderPriceDetails(
          orderPriceRequests,
          PRICING_SERVICE_URL,
          getAccessToken()
        );

  const withFullScreen = (component: JSX.Element): JSX.Element => (
    <Box sx={{ height: "70vh", justifyContent: "center", display: "flex" }}>
      {component}
    </Box>
  );

  const getAccessToken = (): string => {
    const authToken = oktaAuth.getAccessToken();
    if (!authToken) return "";
    return authToken;
  };

  const getOrderLineItems = (promiseOrder: Promise<Order>) =>
    promiseOrder
      .then((savedOrder) => {
        setOrderCurrencyCode(savedOrder.currencyCode);
        return filterOrderLineItems(savedOrder);
      })
      .catch((error) => {
        setApiError(error);
        return [];
      });

  const filterOrderLineItems = (savedOrder: Order): OrderLineItem[] => {
    const lineItems = createOrderLineItemsFromNewOrderType(savedOrder);
    if (lineItems.length === 0) {
      return lineItems;
    }
    return lineItems.filter(
      (lineItem) =>
        !(
          lineItem.effectivePrice === "" ||
          lineItem.status.toLowerCase()?.includes("cancelled") ||
          lineItem.isLineEligibleForM2NO
        )
    );
  };
  const isOrderServiceApiAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_ORDER_SERVICE;
  const orderApiHook = useOrderApi();
  const location = useLocation();
  const searchParams =
    location && location.search
      ? new URLSearchParams(location.search)
      : new URLSearchParams("");
  const accountIdFromUrl = searchParams.get("accountNumber");
  const accountId = accountIdFromUrl;

  const loadSavedOrder = () =>
    isOrderServiceApiAuthEnabled
      ? orderApiHook
          .summary(
            ORDERS_SERVICE_URL,
            orderNumber!,
            accountId as string,
            i18n.language
          )
          .then(convertToNewOrderType)
      : OrderApi.summary(
          ORDERS_SERVICE_URL,
          getAccessToken(),
          orderNumber!,
          accountId as string,
          i18n.language
        ).then(convertToNewOrderType);

  useEffect(() => {
    getConsolidatedPrice();
  }, [orderNumber]);

  const consolidatedPriceBatchAPICall = (
    orderLineItemsBatch: OrderLineItem[],
    orderSummaryDetails: Order
  ) => {
    const consolidatedPricingRequest: PricingWithTaxDetailsRequest[] =
      orderLineItemsBatch.map((orderLineItem) => {
        const getServiceCodeFromNewSLA = () => {
          const newServiceLevel = orderLineItem.newServiceLevelRequested;
          return newServiceLevel?.currentValue?.keys().next().value;
        };

        const getServiceCodeForOldSLA = () =>
          orderLineItem.serviceLevelRequested?.currentValue
            ? getServiceCodeForSLAName(
                orderLineItem.serviceLevelRequested.currentValue!
              )
            : undefined;

        const serviceCode = ENABLE_NEW_SLA_DATA_FOR_PRICING
          ? getServiceCodeFromNewSLA()
          : getServiceCodeForOldSLA();

        return {
          countryCode: dealerLocation!,
          sapDealerId: (accountId ?? dealerAccount?.id)!,
          lineNumber: orderLineItem?.lineNumber[0]!,
          orderType: urlParamsToOrderTypeMapping[orderType!],
          partNumber: orderLineItem?.partNumber!,
          quantity: orderLineItem?.quantity!,
          promotionId: "",
          serviceCode,
          orderNumber,
          orderServiceType: orderSummaryDetails?.orderServiceType,
          discount: orderSummaryDetails?.discount,
        };
      });

    return consolidatedPricingFn(consolidatedPricingRequest);
  };

  const consolidatedOrderPriceCall = () => {
    const request: OrderPriceRequest = {
      countryCode: dealerLocation!,
      sapDealerId: (accountId ?? dealerAccount?.id)!,
      orderNumber: orderNumber!,
      orderType: urlParamsToOrderTypeMapping[orderType!],
    };

    return orderPricingFn(request);
  };

  function createPriceAttribute(
    attributeField: PricingTaxField,
    overallConsolidatedPriceResponse: ConsolidatedPriceResponse
  ) {
    const pricingTaxFieldForAttribute: PricingTaxField = {
      field: attributeField.field,
      value: 0,
    };
    overallConsolidatedPriceResponse.attributes.push(
      pricingTaxFieldForAttribute
    );
    return pricingTaxFieldForAttribute;
  }

  function findPriceAttribute(
    overallConsolidatedPriceResponse: ConsolidatedPriceResponse,
    attributeField: PricingTaxField
  ) {
    return overallConsolidatedPriceResponse.attributes.find(
      (fields) => fields.field === attributeField.field
    );
  }

  const handleAllConsolidatedPriceResponses = (responses: any) => {
    const consolidatedPriceResponsePromises =
      responses as PromiseFulfilledResult<ConsolidatedPriceResponse>[];
    const overallConsolidatedPriceResponse: ConsolidatedPriceResponse = {
      countryCode: consolidatedPriceResponsePromises[0].value.countryCode,
      orderNumber: consolidatedPriceResponsePromises[0].value.orderNumber,
      orderType: consolidatedPriceResponsePromises[0].value.orderType,
      attributes: [],
    };
    consolidatedPriceResponsePromises.forEach((response) =>
      response?.value?.attributes.forEach((attributeField) => {
        let pricingTaxFieldForAttribute: PricingTaxField | undefined =
          findPriceAttribute(overallConsolidatedPriceResponse, attributeField);

        if (!pricingTaxFieldForAttribute) {
          pricingTaxFieldForAttribute = createPriceAttribute(
            attributeField,
            overallConsolidatedPriceResponse
          );
        }
        pricingTaxFieldForAttribute.value = Number(
          (pricingTaxFieldForAttribute.value + attributeField.value).toFixed(2)
        );
      })
    );
    setConsolidatedPriceResponse(overallConsolidatedPriceResponse);
  };

  async function getPriceForProvisionedOrders() {
    const response = await consolidatedOrderPriceCall()
      .catch(() => {
        setIsTechnicalError(true);
      })
      .finally(() => {
        setShowLoadingIndicator(false);
      });
    if (response !== undefined) {
      setConsolidatedPriceResponse(response);
    }
  }

  async function getPriceForNonProvisionedOrders(
    orderLineItems: OrderLineItem[] | any[],
    orderSummaryDetails: Order
  ) {
    const orderLineItemChunks: Array<Array<OrderLineItem>> = [];
    for (
      let i = 0;
      i < orderLineItems.length;
      i += CONSOLIDATED_PRICE_API_BATCH_SIZE
    ) {
      orderLineItemChunks.push(
        orderLineItems.slice(i, i + CONSOLIDATED_PRICE_API_BATCH_SIZE)
      );
    }

    const consolidatedPricingPromises = [];
    /* eslint-disable no-await-in-loop */
    for (
      let i = 0;
      i < orderLineItemChunks.length;
      i += CONSOLIDATED_PRICE_API_MAX_PARALLEL_CALLS
    ) {
      const promise = await Promise.allSettled(
        orderLineItemChunks
          .slice(i, i + CONSOLIDATED_PRICE_API_MAX_PARALLEL_CALLS)
          .map((orderLineItemsInChunk: OrderLineItem[]) =>
            consolidatedPriceBatchAPICall(
              orderLineItemsInChunk,
              orderSummaryDetails
            )
          )
      );
      consolidatedPricingPromises.push(promise);
    }
    Promise.all(consolidatedPricingPromises.flat())
      .then(async (responses) => {
        if (responses.find((result) => result.status === "rejected")) {
          setIsTechnicalError(true);
        } else if (orderLineItemChunks.length === 1) {
          const response = (
            responses[0] as PromiseFulfilledResult<ConsolidatedPriceResponse>
          )?.value;
          setConsolidatedPriceResponse(response);
        } else {
          handleAllConsolidatedPriceResponses(responses);
        }
      })
      .catch(() => {
        setIsTechnicalError(true);
      })
      .finally(() => {
        setShowLoadingIndicator(false);
      });
  }

  const getConsolidatedPrice = async () => {
    setShowLoadingIndicator(true);

    const orderPromise = loadSavedOrder();
    const orderLineItems = await getOrderLineItems(orderPromise);
    if (orderLineItems.length === 0) {
      setShowLoadingIndicator(false);
      return;
    }
    const order = await orderPromise;
    if (
      [OrderStatus.SUBMITTED, OrderStatus.UNSUBMITTED]?.includes(
        order.orderStatus
      )
    ) {
      await getPriceForNonProvisionedOrders(orderLineItems, order);
      return;
    }
    await getPriceForProvisionedOrders();
  };

  const getFieldValue = (fieldName: string) => {
    const value = consolidatedPriceResponse?.attributes
      .filter((field) => field.field === fieldName)
      .map((field) => field.value)[0];

    if (maskPrice) {
      return value !== undefined
        ? "*".repeat(value.toFixed(2).toString().length)
        : "***";
    }

    const price = value !== undefined ? value.toFixed(2).toString() : "0.00";
    return `${currencySymbol} ${price}`;
  };

  type CommonSummaryFieldsConfig = {
    showCorePrice: boolean;
  };
  const commonPriceSummaryFields = (
    config: CommonSummaryFieldsConfig = {
      showCorePrice: false,
    }
  ) => (
    <>
      <Typography sx={costBreakDownSectionSx} data-testid="total-bdp-header">
        {t("orderEstimatedCostSummary.totalValue")}
        {(orderCurrencyCode || dealerLocation) && (
          <span> ({currencyCode}):</span>
        )}
        <Typography
          display="inline"
          component="span"
          sx={costBreakDownValueSx}
          data-testid="total-value"
        >
          {getFieldValue("totalValue")}
        </Typography>
      </Typography>
      {config.showCorePrice && (
        <Typography sx={costBreakDownSectionSx} data-testid="core-price-header">
          {t("orderEstimatedCostSummary.corePrice")}
          <Typography
            display="inline"
            component="span"
            sx={costBreakDownValueSx}
            data-testid="core-price-value"
          >
            {getFieldValue("corePrice")}
          </Typography>
        </Typography>
      )}
      <Typography sx={costBreakDownSectionSx} data-testid="discounts-header">
        {t("orderEstimatedCostSummary.discounts")}
        <Typography
          display="inline"
          component="span"
          sx={costBreakDownValueSx}
          data-testid="discounts-value"
        >
          {getFieldValue("discounts")}
        </Typography>
      </Typography>
      <Typography sx={costBreakDownSectionSx} data-testid="surcharges-header">
        {t("orderEstimatedCostSummary.surcharges")}
        <Typography
          display="inline"
          component="span"
          sx={costBreakDownValueSx}
          data-testid="surcharges-value"
        >
          {getFieldValue("surcharges")}
        </Typography>
      </Typography>
    </>
  );

  const estimatedCostOfOrderSection = () => (
    <Typography sx={costBreakDownHeaderSx} data-testid="total-header">
      {t("orderEstimatedCostSummary.total")}
      <Typography
        display="inline"
        component="span"
        sx={costBreakDownTotalValueSx}
        data-testid="total-estimated-value"
      >
        {getFieldValue("totalEstimatedCost")}
      </Typography>
    </Typography>
  );

  const taxesSection = () => (
    <Typography sx={costBreakDownSectionSx} data-testid="taxes-section">
      {i18n.exists(
        `orders:orderEstimatedCostSummary.${dealerLocation?.toLowerCase()}.taxes`
      )
        ? t(
            `orders:orderEstimatedCostSummary.${dealerLocation?.toLowerCase()}.taxes`
          )
        : t("orders:orderEstimatedCostSummary.taxes")}
      <Typography
        display="inline"
        component="span"
        sx={costBreakDownValueSx}
        data-testid="taxes-value"
      >
        {getFieldValue("taxes")}
      </Typography>
    </Typography>
  );

  const otherTaxesSection = () => (
    <Typography sx={costBreakDownSectionSx} data-testid="other-taxes-section">
      {t("orderEstimatedCostSummary.otherTaxes")}
      <Typography
        display="inline"
        component="span"
        sx={costBreakDownValueSx}
        data-testid="other-taxes-value"
      >
        {getFieldValue("totalOtherTaxes")}
      </Typography>
    </Typography>
  );

  const BrazilConsolidatedPrices = () => (
    <>
      {commonPriceSummaryFields()}
      {taxesSection()}
      {otherTaxesSection()}
      {estimatedCostOfOrderSection()}
    </>
  );

  const IndiaChinaConsolidatedPrices = () => (
    <>
      {commonPriceSummaryFields()}
      {taxesSection()}
      {estimatedCostOfOrderSection()}
    </>
  );

  const USConsolidatedPrices = () => (
    <>
      {commonPriceSummaryFields({ showCorePrice: true })}
      {taxesSection()}
      {estimatedCostOfOrderSection()}
    </>
  );

  const ConsolidatedPrices = useMemo(() => {
    const countrySpecificConsolidatedPrices = {
      BR: () => <BrazilConsolidatedPrices />,
      IN: () => <IndiaChinaConsolidatedPrices />,
      CN: () => <IndiaChinaConsolidatedPrices />,
      US: () => <USConsolidatedPrices />,
    };

    switch (dealerLocation!) {
      case "BR":
      case "IN":
      case "CN":
      case "US":
        return countrySpecificConsolidatedPrices[dealerLocation];
      default:
        return () => <USConsolidatedPrices />;
    }
  }, [dealerLocation, consolidatedPriceResponse]);

  if (showLoadingIndicator) {
    return withFullScreen(<LoadingIndicator />);
  }

  if (isTechnicalError) {
    return withFullScreen(
      <ErrorPage
        title={t("common:technicalError.title")}
        subtitles={[t("common:technicalError.subtitle")]}
        icon={{
          Component: WarningAmberIcon,
          color: "warning",
        }}
        showReloadBtn
        reloadFunction={getConsolidatedPrice}
        showBackToHomeLink
      />
    );
  }

  if (apiError?.response?.status === 404) return <OrderNotFoundError />;

  return (
    <Box
      width="100%"
      paddingTop={theme.tokens.FuelSpacing4}
      data-testid="order-estimated-cost-summary"
    >
      <Typography
        sx={costEstimationHeaderSx}
        data-testid="estimated-cost-of-order-header"
      >
        {t("orderEstimatedCostSummary.title")}
        {(orderCurrencyCode || dealerLocation) && (
          <span> ({currencyCode}):</span>
        )}
      </Typography>
      <Card style={{ margin: theme.tokens.FuelSpacing4 }}>
        <CardContent>
          <ConsolidatedPrices />
          <Alert
            variant="info"
            sx={costHeaderSx}
            title=""
            data-testid="estimation-alert-info"
          >
            <Typography data-testid="estimation-info-text" component="span">
              {t("orderEstimatedCostSummary.estimationInfoText")}
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EstimatedCostSummary;
